import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Filter, Upload, Plus, Users, Trash2, RefreshCw, Loader2, CheckSquare, Square, X, ChevronLeft, ChevronRight, Download, Archive, CheckCircle2 } from 'lucide-react';
import api from '../../services/api';
import LeadCard from '../../components/LeadCard';
import Modal from '../../components/Modal';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import SearchableSelect from '../../components/SearchableSelect';
import { StatusColors, StatusLabels } from '../../constants/colors';
import useAuthStore from '../../store/authStore';

const SALARY_RANGES = [
  { value: '0-25000', label: 'Below ₹25k' },
  { value: '25000-50000', label: '₹25k – 50k' },
  { value: '50000-75000', label: '₹50k – 75k' },
  { value: '75000-100000', label: '₹75k – 1L' },
  { value: '100000-', label: '₹1L+' },
  { value: 'none', label: 'Not Provided' },
];
const EMP_LABELS = { salaried: 'Salaried', self_employed: 'Self-Employed' };
const prettyEmp = (v) => EMP_LABELS[v] || v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const AdminLeads = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sp = (k, d = '') => searchParams.get(k) ?? d;
  const spArr = (k) => { const v = searchParams.get(k); return v ? v.split(',').filter(Boolean) : []; };
  const [leads, setLeads] = useState([]);
  const { user } = useAuthStore();
  const [telecallers, setTelecallers] = useState([]);
  const [historyAssignees, setHistoryAssignees] = useState([]); // includes inactive - Previously Assigned filter only
  const [filterOptions, setFilterOptions] = useState({ cities: [], employments: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(() => sp('q'));
  const [statusFilter, setStatusFilter] = useState(() => sp('status'));
  const [outcomeFilter, setOutcomeFilter] = useState(() => sp('outcome'));
  const [assignedFilter, setAssignedFilter] = useState(() => spArr('assigned')); // multi-select: GP ids + 'unassigned'
  const [cities, setCities] = useState(() => spArr('cities'));
  const [employments, setEmployments] = useState(() => spArr('emp'));
  const [salaryRanges, setSalaryRanges] = useState(() => spArr('salary'));
  // Internal management filters (Admin/Manager/Ops only)
  const [companyFilter, setCompanyFilter] = useState(() => sp('company'));
  const [sourceFilter, setSourceFilter] = useState(() => sp('source'));
  const [uploadedFrom, setUploadedFrom] = useState(() => sp('upfrom'));
  const [uploadedTo, setUploadedTo] = useState(() => sp('upto'));
  const [previousGpFilter, setPreviousGpFilter] = useState(() => spArr('pgp')); // multi-select GP ids (incl inactive)
  const [lastAssignedFrom, setLastAssignedFrom] = useState(() => sp('asgfrom'));
  const [lastAssignedTo, setLastAssignedTo] = useState(() => sp('asgto'));
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  
  // Stats from backend (for accurate filter counts)
  const [statusCounts, setStatusCounts] = useState({});
  const [outcomeCounts, setOutcomeCounts] = useState({});
  
  // Call outcome filters
  const callOutcomes = [
    { id: 'connected', label: 'Connected', color: '#4CAF50' },
    { id: 'no_answer', label: 'No Answer', color: '#F44336' },
    { id: 'switched_off', label: 'Switched Off', color: '#9E9E9E' },
    { id: 'not_connecting', label: 'Not Connecting', color: '#9E9E9E' },
    { id: 'busy', label: 'Busy', color: '#FF9800' },
    { id: 'wrong_number', label: 'Wrong Number', color: '#E91E63' },
    { id: 'voicemail', label: 'Voicemail', color: '#9C27B0' },
    { id: 'never_called', label: 'Never Called', color: '#607D8B' },
  ];
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(() => parseInt(sp('page', '1'), 10) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(() => parseInt(sp('size', '25'), 10) || 25);
  const [pageInput, setPageInput] = useState('');
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  
  // Selection
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selectAllFiltered, setSelectAllFiltered] = useState(false); // NEW: For "select all X matching"
  const [gpSearchQuery, setGpSearchQuery] = useState(''); // Search in assign modal
  
  // Form
  const [newLead, setNewLead] = useState({ name: '', phone: '', email: '', city: '', source: '', company_name: '', notes: '' });
  const [importFile, setImportFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const statuses = ['new', 'not_interested', 'follow_up', 'leads', 'file', 'no_status'];

  // Chip counts must reconcile with the filtered list, so every non-status filter the list uses
  // is applied here too (only the status itself is left out - that is what the chips count).
  const fetchStats = async () => {
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (assignedFilter.length) params.assigned_to = assignedFilter.join(',');
      if (outcomeFilter) {
        if (outcomeFilter === 'never_called') params.never_called = true;
        else params.last_call_outcome = outcomeFilter;
      }
      if (companyFilter) params.company = companyFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (uploadedFrom) params.created_from = uploadedFrom;
      if (uploadedTo) params.created_to = uploadedTo;
      if (previousGpFilter.length) params.previous_gp = previousGpFilter.join(',');
      if (lastAssignedFrom) params.last_assigned_from = lastAssignedFrom;
      if (lastAssignedTo) params.last_assigned_to = lastAssignedTo;
      if (cities.length) params.cities = cities.join(',');
      if (employments.length) params.employments = employments.join(',');
      if (salaryRanges.length) params.salary_ranges = salaryRanges.join(',');

      const response = await api.get('/leads/stats', { params });
      setStatusCounts(response.data.by_status || {});
      setOutcomeCounts(response.data.by_outcome || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchData = async (page = currentPage, size = pageSize) => {
    try {
      const params = {
        page: page,
        page_size: size,
      };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;
      if (outcomeFilter) {
        if (outcomeFilter === 'never_called') {
          params.never_called = true;
        } else {
          params.last_call_outcome = outcomeFilter;
        }
      }
      if (assignedFilter.length) params.assigned_to = assignedFilter.join(',');
      if (companyFilter) params.company = companyFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (uploadedFrom) params.created_from = uploadedFrom;
      if (uploadedTo) params.created_to = uploadedTo;
      if (previousGpFilter.length) params.previous_gp = previousGpFilter.join(',');
      if (lastAssignedFrom) params.last_assigned_from = lastAssignedFrom;
      if (lastAssignedTo) params.last_assigned_to = lastAssignedTo;
      if (cities.length) params.cities = cities.join(',');
      if (employments.length) params.employments = employments.join(',');
      if (salaryRanges.length) params.salary_ranges = salaryRanges.join(',');
      
      const [leadsRes, telecallersRes] = await Promise.all([
        api.get('/leads', { params }),
        api.get('/users/telecallers'),
      ]);
      
      // Handle paginated response
      const leadsData = leadsRes.data;
      setLeads(leadsData.leads || []);
      setTotalCount(leadsData.pagination?.total_count || 0);
      setTotalPages(leadsData.pagination?.total_pages || 1);
      setCurrentPage(leadsData.pagination?.page || 1);
      
      setTelecallers(telecallersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Re-fetch when filters change. A signature compare (instead of a boolean mount ref) keeps
  // the restored page on first load AND survives React StrictMode's double-invoke in dev:
  // only a genuine filter change resets to page 1.
  const filterSig = JSON.stringify([searchQuery, statusFilter, outcomeFilter, assignedFilter, companyFilter, sourceFilter, uploadedFrom, uploadedTo, previousGpFilter, lastAssignedFrom, lastAssignedTo, cities, employments, salaryRanges]);
  const prevSig = useRef(null);
  useEffect(() => {
    if (prevSig.current === null || prevSig.current === filterSig) {
      // First mount (or StrictMode re-run with identical filters): honour the current page.
      prevSig.current = filterSig;
      fetchData(currentPage, pageSize);
      fetchStats();
      return;
    }
    prevSig.current = filterSig;
    setCurrentPage(1); // Reset to page 1 when filters actually change
    fetchData(1, pageSize);
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSig]);

  // Mirror all list state into the URL so refresh / browser Back / returning from a lead
  // detail restores the exact view. Uses replace so filter tweaks don't spam history.
  useEffect(() => {
    const p = {};
    if (searchQuery) p.q = searchQuery;
    if (statusFilter) p.status = statusFilter;
    if (outcomeFilter) p.outcome = outcomeFilter;
    if (assignedFilter.length) p.assigned = assignedFilter.join(',');
    if (companyFilter) p.company = companyFilter;
    if (sourceFilter) p.source = sourceFilter;
    if (cities.length) p.cities = cities.join(',');
    if (employments.length) p.emp = employments.join(',');
    if (salaryRanges.length) p.salary = salaryRanges.join(',');
    if (uploadedFrom) p.upfrom = uploadedFrom;
    if (uploadedTo) p.upto = uploadedTo;
    if (previousGpFilter.length) p.pgp = previousGpFilter.join(',');
    if (lastAssignedFrom) p.asgfrom = lastAssignedFrom;
    if (lastAssignedTo) p.asgto = lastAssignedTo;
    if (currentPage > 1) p.page = String(currentPage);
    if (pageSize !== 25) p.size = String(pageSize);
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, outcomeFilter, assignedFilter, companyFilter, sourceFilter, cities, employments, salaryRanges, uploadedFrom, uploadedTo, previousGpFilter, lastAssignedFrom, lastAssignedTo, currentPage, pageSize]);

  // Role-scoped distinct City / Employment options for the filter dropdowns.
  useEffect(() => {
    api.get('/leads/filter-options').then((r) => setFilterOptions(r.data || { cities: [], employments: [] })).catch(() => {});
  }, []);

  // Previously-Assigned dropdown must include INACTIVE GPs (historical assignees). Fetched
  // once for Admin/Manager/Ops only; never used for the current-assignee dropdown.
  useEffect(() => {
    if (!['admin', 'manager', 'ops'].includes(user?.role)) return;
    api.get('/users/telecallers', { params: { include_inactive: true } })
      .then((res) => setHistoryAssignees(res.data || []))
      .catch(() => {});
  }, [user?.role]);

  // Get count from stats endpoint
  const getStatusCount = (status) => statusCounts[status] || 0;
  const getOutcomeCount = (outcomeId) => outcomeCounts[outcomeId] || 0;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setIsLoading(true);
      setCurrentPage(newPage);
      fetchData(newPage);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setIsLoading(true);
    fetchData(1, newSize);
  };

  const handleJumpToPage = () => {
    const n = parseInt(pageInput, 10);
    if (!isNaN(n)) {
      handlePageChange(Math.min(Math.max(n, 1), totalPages));
    }
    setPageInput('');
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchData();
    fetchStats();
  };

  const handleLeadPress = (lead) => {
    if (selectMode) {
      toggleLeadSelection(lead.id);
    } else {
      navigate(`/admin/leads/${lead.id}`);
    }
  };

  const handleCall = (lead) => {
    let phone = lead.phone.replace(/[^0-9+]/g, '');
    if (!phone.startsWith('+')) {
      phone = '+91' + phone;
    }
    window.location.href = `tel:${phone}`;
  };

  const toggleLeadSelection = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const selectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
      setSelectAllFiltered(false);
    } else {
      setSelectedLeads(leads.map(l => l.id));
    }
  };

  const activeFilterCount =
    [statusFilter, outcomeFilter, companyFilter, sourceFilter, uploadedFrom, uploadedTo, lastAssignedFrom, lastAssignedTo, searchQuery].filter(Boolean).length +
    assignedFilter.length + cities.length + employments.length + salaryRanges.length + previousGpFilter.length;

  const clearAllFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setOutcomeFilter('');
    setAssignedFilter([]);
    setCities([]);
    setEmployments([]);
    setSalaryRanges([]);
    setCompanyFilter('');
    setSourceFilter('');
    setUploadedFrom('');
    setUploadedTo('');
    setPreviousGpFilter([]);
    setLastAssignedFrom('');
    setLastAssignedTo('');
  };

  // Build the full active-filter payload for bulk ops (select-all / export / archive) so they
  // operate on the complete filtered dataset, not just the visible page.
  const buildBulkFilters = () => {
    const filters = {};
    if (statusFilter) filters.statuses = statusFilter;
    if (assignedFilter.length) filters.assigned_to = assignedFilter.join(',');
    if (searchQuery) filters.search = searchQuery;
    if (outcomeFilter) {
      if (outcomeFilter === 'never_called') filters.never_called = true;
      else filters.outcomes = outcomeFilter;
    }
    if (companyFilter) filters.company = companyFilter;
    if (sourceFilter) filters.source = sourceFilter;
    if (uploadedFrom) filters.created_from = uploadedFrom;
    if (uploadedTo) filters.created_to = uploadedTo;
    if (previousGpFilter.length) filters.previous_gp = previousGpFilter.join(',');
    if (lastAssignedFrom) filters.last_assigned_from = lastAssignedFrom;
    if (lastAssignedTo) filters.last_assigned_to = lastAssignedTo;
    if (cities.length) filters.cities = cities.join(',');
    if (employments.length) filters.employments = employments.join(',');
    if (salaryRanges.length) filters.salary_ranges = salaryRanges.join(',');
    return filters;
  };

  // NEW: Select all leads matching current filters (database-level)
  const handleSelectAllFiltered = async () => {
    if (selectAllFiltered) {
      setSelectAllFiltered(false);
      setSelectedLeads([]);
      return;
    }
    
    setIsSubmitting(true);
    try {
      const filters = buildBulkFilters();
      
      const response = await api.post('/leads/select-all-ids', filters);
      setSelectedLeads(response.data.lead_ids);
      setSelectAllFiltered(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to select all');
    } finally {
      setIsSubmitting(false);
    }
  };

  // NEW: Export leads to Excel
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const filters = buildBulkFilters();
      
      const response = await api.post('/leads/export', filters, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to export leads');
    } finally {
      setIsExporting(false);
    }
  };

  // NEW: Archive selected leads
  const handleArchive = async () => {
    if (selectedLeads.length === 0) return;
    
    if (window.confirm(`Archive ${selectAllFiltered ? totalCount : selectedLeads.length} leads?`)) {
      setIsSubmitting(true);
      try {
        if (selectAllFiltered) {
          // Archive by filter
          const filters = buildBulkFilters();
          
          await api.post('/leads/archive', { filters, archive: true });
        } else {
          // Archive by IDs
          await api.post('/leads/archive', { lead_ids: selectedLeads, archive: true });
        }
        setSelectedLeads([]);
        setSelectMode(false);
        setSelectAllFiltered(false);
        fetchData();
      } catch (error) {
        alert(error.response?.data?.detail || 'Failed to archive leads');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleAddLead = async () => {
    if (!newLead.name.trim() || !newLead.phone.trim()) {
      alert('Name and phone are required');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/leads', newLead);
      setShowAddModal(false);
      setNewLead({ name: '', phone: '', email: '', city: '', source: '', company_name: '', notes: '' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add lead');
    } finally {
      setIsSubmitting(false);
    }
  };

  const [importResult, setImportResult] = useState(null);
  const [importPreview, setImportPreview] = useState(null);

  const handlePreview = async () => {
    if (!importFile) {
      alert('Please select a file');
      return;
    }
    setIsSubmitting(true);
    setImportPreview(null);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await api.post('/leads/import?dry_run=true', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportPreview(response.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to preview import');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Please select a file');
      return;
    }

    setIsSubmitting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await api.post('/leads/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(response.data);
      setImportPreview(null);
      setImportFile(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to import leads');
    } finally {
      setIsSubmitting(false);
    }
  };

  const assignOne = async (leadId, telecallerId) => {
    if (!telecallerId) return;
    try {
      await api.post('/leads/assign', { lead_ids: [leadId], user_id: telecallerId });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to assign lead');
    }
  };

  const handleAssign = async (telecallerId) => {    if (selectedLeads.length === 0) return;

    setIsSubmitting(true);
    try {
      await api.post('/leads/assign', {
        lead_ids: selectedLeads,
        user_id: telecallerId,
      });
      setShowAssignModal(false);
      setSelectedLeads([]);
      setSelectMode(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to assign leads');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAutoDistribute = async () => {
    if (selectedLeads.length === 0) return;

    setIsSubmitting(true);
    try {
      await api.post('/leads/auto-distribute', {
        lead_ids: selectedLeads,
      });
      alert('Leads distributed successfully');
      setShowAssignModal(false);
      setSelectedLeads([]);
      setSelectMode(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to distribute leads');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedLeads.length} leads?`)) {
      setIsSubmitting(true);
      try {
        await api.post('/leads/bulk-delete', { lead_ids: selectedLeads });
        setSelectedLeads([]);
        setSelectMode(false);
        fetchData();
      } catch (error) {
        alert(error.response?.data?.detail || 'Failed to delete leads');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="admin-leads">
      {/* Header */}
      <div className="p-4 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center gap-1 text-sm py-2"
            data-testid="add-lead-btn"
          >
            <Plus size={18} />
            Add
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="btn-secondary flex items-center gap-1 text-sm py-2"
            data-testid="import-btn"
          >
            <Upload size={18} />
            Import
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting || totalCount === 0}
            className="btn-secondary flex items-center gap-1 text-sm py-2"
            data-testid="export-btn"
          >
            {isExporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
            Export
          </button>
          <button
            onClick={() => setSelectMode(!selectMode)}
            className={`flex items-center gap-1 text-sm py-2 px-3 rounded-lg transition-colors ${
              selectMode ? 'bg-green-100 text-green-700' : 'btn-secondary'
            }`}
            data-testid="select-mode-btn"
          >
            {selectMode ? <CheckSquare size={18} /> : <Square size={18} />}
            Select
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search data..."
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              data-testid="search-input"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg border transition-colors ${
              statusFilter || outcomeFilter || assignedFilter ? 'bg-green-50 border-green-600 text-green-600' : 'border-gray-200 text-gray-600'
            }`}
          >
            <Filter size={20} />
          </button>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2 rounded-lg border border-gray-200 text-gray-600"
          >
            <RefreshCw size={20} className={isRefreshing ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setStatusFilter('')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                  !statusFilter ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                All Status ({Object.values(statusCounts).reduce((a, b) => a + b, 0) || totalCount})
              </button>
              {statuses.map((status) => {
                const count = getStatusCount(status);
                return (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize ${
                      statusFilter === status ? 'text-white' : 'bg-gray-100 text-gray-700'
                    }`}
                    style={{ backgroundColor: statusFilter === status ? StatusColors[status] : undefined }}
                  >
                    {StatusLabels[status] || status.replace('_', ' ')} {count > 0 && `(${count})`}
                  </button>
                );
              })}
            </div>
            
            {/* Call Outcome Filters */}
            <div className="flex flex-wrap gap-2">
              <span className="px-2 py-1.5 text-xs font-medium text-gray-500 uppercase">Call Outcome:</span>
              <button
                onClick={() => setOutcomeFilter('')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                  !outcomeFilter ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                All Outcomes
              </button>
              {callOutcomes.map((outcome) => {
                const count = getOutcomeCount(outcome.id);
                return (
                  <button
                    key={outcome.id}
                    onClick={() => setOutcomeFilter(outcome.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                      outcomeFilter === outcome.id ? 'text-white' : 'bg-gray-100 text-gray-700'
                    }`}
                    style={{ backgroundColor: outcomeFilter === outcome.id ? outcome.color : undefined }}
                  >
                    {outcome.label} {count > 0 && `(${count})`}
                  </button>
                );
              })}
            </div>
            {/* Current Assigned To - multi-select (OR within, AND with other filters). Includes Unassigned. */}
            <MultiSelectDropdown
              testId="filter-assigned"
              placeholder="Assigned To"
              options={[
                { value: 'unassigned', label: 'Unassigned' },
                ...telecallers.map((tc) => ({ value: tc.id, label: `${tc.name || tc.full_name} (${tc.email?.split('@')[0]})` })),
              ]}
              selected={assignedFilter}
              onChange={setAssignedFilter}
            />

            {/* Advanced lead filters: City (searchable), Employment, Salary Range */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2" data-testid="advanced-filters">
              <MultiSelectDropdown
                testId="filter-city"
                placeholder="City"
                searchable
                options={[
                  { value: 'none', label: 'Not Provided' },
                  ...filterOptions.cities.map((c) => ({ value: c, label: c })),
                ]}
                selected={cities}
                onChange={setCities}
              />
              <MultiSelectDropdown
                testId="filter-employment"
                placeholder="Employment"
                options={[
                  ...filterOptions.employments.map((e) => ({ value: e, label: prettyEmp(e) })),
                  { value: 'none', label: 'Not Provided' },
                ]}
                selected={employments}
                onChange={setEmployments}
              />
              <MultiSelectDropdown
                testId="filter-salary"
                placeholder="Salary Range"
                options={SALARY_RANGES}
                selected={salaryRanges}
                onChange={setSalaryRanges}
              />
            </div>

            {/* Internal management filters - Admin/Manager/Ops only */}
            {['admin', 'manager', 'ops'].includes(user?.role) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2" data-testid="mgmt-filters">
                <input type="text" value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}
                  placeholder="Company" className="input-field text-sm" data-testid="filter-company" />
                <input type="text" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
                  placeholder="Source" className="input-field text-sm" data-testid="filter-source" />
                {/* Previously Assigned To - historical, multi-select, INCLUDES inactive GPs */}
                <MultiSelectDropdown
                  testId="filter-previous-gp"
                  placeholder="Previously Assigned To"
                  options={(historyAssignees.length ? historyAssignees : telecallers).map((tc) => ({
                    value: tc.id,
                    label: tc.name || tc.full_name,
                    hint: tc.is_active === false ? 'Inactive' : undefined,
                  }))}
                  selected={previousGpFilter}
                  onChange={setPreviousGpFilter}
                />
                <div className="flex items-center gap-1">
                  <label className="text-xs text-gray-500 whitespace-nowrap">Uploaded</label>
                  <input type="date" value={uploadedFrom} onChange={(e) => setUploadedFrom(e.target.value)}
                    className="input-field text-xs" data-testid="filter-uploaded-from" />
                  <input type="date" value={uploadedTo} onChange={(e) => setUploadedTo(e.target.value)}
                    className="input-field text-xs" data-testid="filter-uploaded-to" />
                </div>
                <div className="flex items-center gap-1">
                  <label className="text-xs text-gray-500 whitespace-nowrap">Assigned</label>
                  <input type="date" value={lastAssignedFrom} onChange={(e) => setLastAssignedFrom(e.target.value)}
                    className="input-field text-xs" data-testid="filter-lastassigned-from" />
                  <input type="date" value={lastAssignedTo} onChange={(e) => setLastAssignedTo(e.target.value)}
                    className="input-field text-xs" data-testid="filter-lastassigned-to" />
                </div>
              </div>
            )}

            {/* Active filters summary + clear-all */}
            {activeFilterCount > 0 && (
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100" data-testid="active-filters-bar">
                <span className="text-xs text-gray-500">{activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} applied</span>
                <button
                  onClick={clearAllFilters}
                  data-testid="clear-filters-btn"
                  className="flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700"
                >
                  <X className="w-3.5 h-3.5" /> Clear Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selection Actions */}
      {selectMode && (
        <div className="p-3 bg-green-50 border-b border-green-200 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedLeads.length === leads.length && leads.length > 0}
                onChange={selectAll}
                className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span className="text-sm text-green-700 font-medium">
                Select All on Page ({leads.length})
              </span>
            </label>
            {totalCount > leads.length && (
              <button 
                onClick={handleSelectAllFiltered} 
                className={`text-sm font-medium hover:underline ${selectAllFiltered ? 'text-blue-700' : 'text-green-700'}`}
                disabled={isSubmitting}
              >
                {selectAllFiltered ? `✓ All ${totalCount} selected` : `Select all ${totalCount} matching`}
              </button>
            )}
            {selectedLeads.length > 0 && (
              <span className="text-green-700 font-semibold">
                {selectAllFiltered ? `${totalCount} selected` : `${selectedLeads.length} selected`}
              </span>
            )}
          </div>
          {selectedLeads.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAssignModal(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium"
                data-testid="assign-selected-btn"
              >
                <Users size={16} />
                Assign
              </button>
              <button
                onClick={handleArchive}
                className="flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-sm font-medium"
                data-testid="archive-selected-btn"
              >
                <Archive size={16} />
                Archive
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm font-medium"
                data-testid="delete-selected-btn"
              >
                <Trash2 size={16} />
              </button>
              <button
                onClick={() => { setSelectedLeads([]); setSelectMode(false); setSelectAllFiltered(false); }}
                className="p-1.5 text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Data Count & Pagination Info */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <p className="text-sm text-gray-600">
          <span className="font-semibold">{totalCount}</span> total data
          {totalPages > 1 && (
            <span className="ml-2 text-gray-500">
              (Page {currentPage} of {totalPages})
            </span>
          )}
        </p>
        
        {/* Pagination Controls */}
        <div className="flex items-center gap-3">
          {/* Page size selector */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Per page</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
              disabled={isLoading}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              data-testid="page-size-select"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>

          {totalPages > 1 && (
            <>
              {/* Jump to page */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Go to</span>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleJumpToPage(); }}
                  placeholder={`${currentPage}`}
                  disabled={isLoading}
                  className="w-16 text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  data-testid="page-jump-input"
                />
                <button
                  onClick={handleJumpToPage}
                  disabled={isLoading}
                  className="text-sm px-2.5 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-40"
                  data-testid="page-jump-btn"
                >
                  Go
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1 || isLoading}
                  className="p-1.5 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="text-sm text-gray-700 min-w-[60px] text-center">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages || isLoading}
                  className="p-1.5 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
                  data-testid="next-page-btn"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Data List */}
      <div className="flex-1 overflow-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No data found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {leads.map((lead) => (
              <div key={lead.id} className="relative">
                {selectMode && (
                  <button
                    onClick={() => toggleLeadSelection(lead.id)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-1"
                  >
                    {selectedLeads.includes(lead.id) ? (
                      <CheckSquare size={22} className="text-green-600" />
                    ) : (
                      <Square size={22} className="text-gray-400" />
                    )}
                  </button>
                )}
                <div className={selectMode ? 'pl-10' : ''}>
                  <LeadCard
                    lead={lead}
                    onPress={() => handleLeadPress(lead)}
                    onCall={() => handleCall(lead)}
                    showAssignment={true}
                    assignableAgents={telecallers}
                    onAssign={assignOne}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Lead Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add New Lead">
        <div className="p-4 space-y-4">
          <input
            type="text"
            value={newLead.name}
            onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
            placeholder="Name *"
            className="input-field"
            data-testid="lead-name-input"
          />
          <input
            type="tel"
            value={newLead.phone}
            onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
            placeholder="Phone *"
            className="input-field"
            data-testid="lead-phone-input"
          />
          <input
            type="email"
            value={newLead.email}
            onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
            placeholder="Email"
            className="input-field"
          />
          <input
            type="text"
            value={newLead.city}
            onChange={(e) => setNewLead({ ...newLead, city: e.target.value })}
            placeholder="City"
            className="input-field"
          />
          <input
            type="text"
            value={newLead.source}
            onChange={(e) => setNewLead({ ...newLead, source: e.target.value })}
            placeholder="Source (internal)"
            className="input-field"
          />
          <input
            type="text"
            value={newLead.company_name}
            onChange={(e) => setNewLead({ ...newLead, company_name: e.target.value })}
            placeholder="Company Name"
            className="input-field"
            data-testid="add-lead-company"
          />
          <textarea
            value={newLead.notes}
            onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
            placeholder="Notes"
            className="input-field min-h-[80px] resize-none"
          />
          <button
            onClick={handleAddLead}
            disabled={isSubmitting}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="submit-lead-btn"
          >
            {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Add Lead'}
          </button>
        </div>
      </Modal>

      {/* Import Modal */}
      <Modal isOpen={showImportModal} onClose={() => { setShowImportModal(false); setImportResult(null); setImportPreview(null); setImportFile(null); }} title="Import Leads">
        <div className="p-4 space-y-4">
          {importResult ? (
            <div className="space-y-4">
              <div className="text-center">
                <CheckCircle2 size={48} className="mx-auto text-green-500 mb-3" />
                <h3 className="text-xl font-bold text-gray-900">Import Complete</h3>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{importResult.total_imported}</p>
                  <p className="text-sm text-green-700">New Imported</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600">{importResult.assigned || 0}</p>
                  <p className="text-sm text-blue-700">Assigned</p>
                </div>
                {importResult.reassigned > 0 && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-indigo-600">{importResult.reassigned}</p>
                    <p className="text-sm text-indigo-700">Reassigned to new GP</p>
                  </div>
                )}
                {importResult.protected > 0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-purple-600">{importResult.protected}</p>
                    <p className="text-sm text-purple-700">Leads/Files Protected</p>
                  </div>
                )}
                {importResult.duplicates > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-yellow-600">{importResult.duplicates}</p>
                    <p className="text-sm text-yellow-700">Skipped</p>
                  </div>
                )}
                {importResult.suppressed > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-600">{importResult.suppressed}</p>
                    <p className="text-sm text-red-700">Suppressed</p>
                  </div>
                )}
              </div>

              {importResult.unassigned_telecallers?.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-orange-800 mb-1">⚠️ Unmatched Growth Partners:</p>
                  <p className="text-orange-700">{importResult.unassigned_telecallers.join(', ')}</p>
                </div>
              )}

              <button
                onClick={() => { setShowImportModal(false); setImportResult(null); setImportPreview(null); }}
                className="btn-primary w-full"
                data-testid="import-done-btn"
              >
                Done
              </button>
            </div>
          ) : importPreview ? (
            <div className="space-y-4" data-testid="import-preview">
              <div className="text-center">
                <h3 className="text-lg font-bold text-gray-900">Import Preview</h3>
                <p className="text-sm text-gray-500">{importPreview.total_rows} rows in file — review before committing</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center" data-testid="preview-new">
                  <p className="text-2xl font-bold text-green-600">{importPreview.total_imported}</p>
                  <p className="text-sm text-green-700">Will be created</p>
                </div>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-center" data-testid="preview-reassign">
                  <p className="text-2xl font-bold text-indigo-600">{importPreview.reassigned || 0}</p>
                  <p className="text-sm text-indigo-700">Reassigned to new GP</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center" data-testid="preview-protected">
                  <p className="text-2xl font-bold text-purple-600">{importPreview.protected || 0}</p>
                  <p className="text-sm text-purple-700">Leads/Files protected</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center" data-testid="preview-skipped">
                  <p className="text-2xl font-bold text-yellow-600">{importPreview.duplicates || 0}</p>
                  <p className="text-sm text-yellow-700">Skipped (no target GP)</p>
                </div>
                {importPreview.suppressed > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-600">{importPreview.suppressed}</p>
                    <p className="text-sm text-red-700">Suppressed</p>
                  </div>
                )}
              </div>

              {importPreview.unassigned_telecallers?.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm" data-testid="preview-unmatched-gps">
                  <p className="font-medium text-orange-800 mb-1">⚠️ Unmatched GP names (check for typos in the telecaller column):</p>
                  <p className="text-orange-700">{importPreview.unassigned_telecallers.join(', ')}</p>
                  <p className="text-orange-600 mt-1 text-xs">New rows for these will be created <strong>unassigned</strong>. Existing Data leads with these names will be left unchanged.</p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setImportPreview(null)}
                  disabled={isSubmitting}
                  className="btn-secondary flex-1"
                  data-testid="preview-back-btn"
                >
                  Back
                </button>
                <button
                  onClick={handleImport}
                  disabled={isSubmitting}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  data-testid="confirm-import-btn"
                >
                  {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Confirm Import'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="hidden"
                  id="file-upload"
                  data-testid="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload size={40} className="mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600">
                    {importFile ? importFile.name : 'Click to upload CSV or Excel file'}
                  </p>
                </label>
              </div>
              <div className="text-sm text-gray-500">
                <p className="font-medium mb-2">Required columns:</p>
                <p>• name, phone</p>
                <p className="font-medium mt-2 mb-1">Optional columns:</p>
                <p>• email, source, city, status, notes, telecaller</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium mb-1">🔎 Preview before import</p>
                <p>Existing <strong>Leads/Files</strong> are protected. Existing <strong>Data</strong> leads are reassigned to the GP in the file. New phone numbers create new entries.</p>
              </div>
              <button
                onClick={handlePreview}
                disabled={isSubmitting || !importFile}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="submit-import-btn"
              >
                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Preview Import'}
              </button>
            </>
          )}
        </div>
      </Modal>

      {/* Assign Modal */}
      <Modal isOpen={showAssignModal} onClose={() => { setShowAssignModal(false); setGpSearchQuery(''); }} title="Assign to Growth Partner">
        <div className="p-4">
          <p className="text-gray-600 mb-4">
            Assign <span className="font-semibold">{selectedLeads.length}</span> leads to:
          </p>
          
          {/* Search Growth Partners */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={gpSearchQuery}
              onChange={(e) => setGpSearchQuery(e.target.value)}
              placeholder="Search growth partner..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
              data-testid="gp-search-input"
            />
          </div>
          
          <div className="space-y-2 mb-4 max-h-60 overflow-auto">
            {telecallers
              .filter(tc => 
                !gpSearchQuery || 
                tc.name?.toLowerCase().includes(gpSearchQuery.toLowerCase()) ||
                tc.email?.toLowerCase().includes(gpSearchQuery.toLowerCase())
              )
              .map((tc) => (
              <button
                key={tc.id}
                onClick={() => handleAssign(tc.id)}
                disabled={isSubmitting}
                className="w-full p-3 text-left bg-gray-50 hover:bg-green-50 hover:border-green-200 border border-transparent rounded-lg transition-colors"
              >
                <p className="font-medium text-gray-900">{tc.name}</p>
                <p className="text-sm text-gray-500">{tc.email}</p>
              </button>
            ))}
            {telecallers.filter(tc => 
              !gpSearchQuery || 
              tc.name?.toLowerCase().includes(gpSearchQuery.toLowerCase()) ||
              tc.email?.toLowerCase().includes(gpSearchQuery.toLowerCase())
            ).length === 0 && (
              <p className="text-center text-gray-500 py-4">No growth partners found</p>
            )}
          </div>
          <button
            onClick={handleAutoDistribute}
            disabled={isSubmitting || telecallers.length === 0}
            className="w-full btn-secondary flex items-center justify-center gap-2"
          >
            {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Auto Distribute to Growth Partners'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminLeads;
